/** Primitivas de formulario y resultado compartidas por las calculadoras. */
import { useEffect, useId, useState } from "react";
import { Check, Copy } from "lucide-react";
import { formatoMXN } from "../lib/calc.js";

export function CampoNumero({ label, valor, onChange, sufijo, ayuda, min, max, step = "any", prefijo }) {
  const id = useId();
  // Texto crudo: permite vaciar el campo mientras se escribe sin que salte un "0".
  const [texto, setTexto] = useState(Number.isFinite(valor) ? String(valor) : "");
  useEffect(() => {
    if (Number(texto) !== valor && !(texto === "" && valor === 0)) setTexto(Number.isFinite(valor) ? String(valor) : "");
  }, [valor]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <label htmlFor={id} className="etiqueta">{label}</label>
      <div className="relative">
        {prefijo && (
          <span className="texto-3 pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm">
            {prefijo}
          </span>
        )}
        <input
          id={id}
          type="number"
          inputMode={step === "1" || step === 1 ? "numeric" : "decimal"}
          min={min}
          max={max}
          step={step}
          className={`campo ${prefijo ? "pl-7" : ""} ${sufijo ? "pr-12" : ""}`}
          value={texto}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            const v = e.target.value;
            setTexto(v);
            let n = v === "" ? 0 : Number(v);
            if (min != null && n < min) n = min;
            if (max != null && n > max) n = max;
            if (v !== "" && String(n) !== v && Number(v) !== n) setTexto(String(n));
            onChange(n);
          }}
        />
        {sufijo && (
          <span className="texto-3 pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-sm">
            {sufijo}
          </span>
        )}
      </div>
      {ayuda && <span className="texto-3 mt-1.5 block text-2xs">{ayuda}</span>}
    </div>
  );
}

export function CampoSelect({ label, valor, onChange, opciones, ayuda }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="etiqueta">{label}</label>
      <select id={id} className="campo" value={valor} onChange={(e) => onChange(e.target.value)}>
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>{o.etiqueta}</option>
        ))}
      </select>
      {ayuda && <span className="texto-3 mt-1.5 block text-2xs">{ayuda}</span>}
    </div>
  );
}

export function Pestanas({ valor, onChange, opciones }) {
  return (
    <div
      role="tablist"
      className="superficie-2 borde scrollbar-none -mx-1 flex max-w-full gap-1 overflow-x-auto rounded-xl border p-1 sm:inline-flex"
    >
      {opciones.map((o) => (
        <button
          key={o.valor}
          role="tab"
          type="button"
          aria-selected={valor === o.valor}
          onClick={() => onChange(o.valor)}
          className={`shrink-0 whitespace-nowrap rounded-lg px-3.5 py-2.5 text-sm font-semibold transition active:scale-[0.98] ${
            valor === o.valor
              ? "superficie text-[color:var(--acento-texto)] shadow-[var(--sombra-1)]"
              : "texto-2 hover:text-[color:var(--texto)]"
          }`}
        >
          {o.etiqueta}
        </button>
      ))}
    </div>
  );
}

/**
 * El número que la persona vino a ver.
 *
 * Es el único elemento de la página con este peso visual: en una
 * calculadora, si el resultado compite con los campos, la herramienta
 * falla en lo único que tenía que hacer.
 */
export function Resultado({ label, valor, nota, tono = "acento", copiable = true }) {
  const [copiado, setCopiado] = useState(false);

  const tonos = {
    acento: "border-[color:var(--color-marca-200)] bg-[color:var(--acento-suave)] text-[color:var(--acento-texto)]",
    positivo: "border-[color:var(--positivo-borde)] bg-[color:var(--positivo-bg)] text-[color:var(--positivo)]",
    aviso: "border-[color:var(--aviso-borde)] bg-[color:var(--aviso-bg)] text-[color:var(--aviso)]",
    alerta: "border-[color:var(--alerta-borde)] bg-[color:var(--alerta-bg)] text-[color:var(--alerta)]",
  };

  async function copiar() {
    try {
      await navigator.clipboard.writeText(String(valor));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      /* sin permiso de portapapeles: no hacemos ruido */
    }
  }

  return (
    <div className={`ticket ${tono !== "acento" ? `ticket-${tono}` : ""}`}>
      <span className="block text-2xs font-bold uppercase tracking-[0.1em] opacity-70">{label}</span>
      <strong key={valor} className="ticket-cifra cifra mt-2 block font-mono text-[2rem] leading-none font-bold sm:text-[2.5rem]">
        {valor}
      </strong>
      {nota && <span className="mt-3 block text-xs opacity-75">{nota}</span>}

      {copiable && (
        <button
          type="button"
          onClick={copiar}
          aria-label={`Copiar ${label}`}
          className="no-imprimir absolute right-3 top-3 rounded-lg p-2.5 opacity-60 transition hover:bg-white/10 hover:opacity-100 active:translate-y-px"
        >
          {copiado ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}

export function Dato({ label, valor, destacar = false }) {
  return (
    <div className="flex items-baseline gap-2 py-2">
      <span className={`text-sm ${destacar ? "font-semibold" : "texto-2"}`}>{label}</span>
      <span aria-hidden="true" className="borde mb-1 min-w-4 flex-1 border-b border-dotted" />
      <span
        className={`cifra font-mono text-sm ${destacar ? "font-bold text-[color:var(--texto)]" : "texto-2"}`}
      >
        {valor}
      </span>
    </div>
  );
}

export const mxn = formatoMXN;

/**
 * Rejilla de la calculadora: campos a la izquierda, resultado a la
 * derecha. En móvil se apila, y el resultado va primero — quien entra
 * desde el teléfono debe ver su número sin hacer scroll.
 */
export function Rejilla({ children, cols = "lg:grid-cols-3" }) {
  return <div className={`grid grid-cols-1 gap-6 ${cols}`}>{children}</div>;
}

/** Envuelve el bloque de resultado para que suba en móvil. */
export function PanelResultado({ children, className = "" }) {
  return <div className={`order-first space-y-3 lg:order-last lg:col-span-2 ${className}`}>{children}</div>;
}

export function PanelCampos({ children }) {
  return <div className="space-y-4">{children}</div>;
}

/** Tabla de referencia: datos oficiales que la gente viene a consultar. */
export function TablaReferencia({ titulo, columnas, filas, nota }) {
  return (
    <figure className="tarjeta overflow-hidden">
      {titulo && (
        <figcaption className="borde superficie-2 border-b px-4 py-2.5 text-sm font-semibold">
          {titulo}
        </figcaption>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="superficie-2 texto-2 text-2xs uppercase tracking-wide">
            <tr>
              {columnas.map((c, i) => (
                <th key={c} scope="col" className={`px-4 py-2 font-semibold ${i > 0 ? "text-right" : ""}`}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="cifra font-mono">
            {filas.map((f, i) => (
              <tr key={i} className="borde border-t">
                {f.map((celda, j) => (
                  <td key={j} className={`px-4 py-2 ${j > 0 ? "text-right" : "texto-2"}`}>
                    {celda}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {nota && <p className="texto-3 borde border-t px-4 py-2 text-2xs">{nota}</p>}
    </figure>
  );
}
